// Helpers de formatage centralisés (FR / Côte d'Ivoire par défaut).
// Locale-aware : lit document.documentElement.lang ('fr' ou 'en')
// pour basculer entre 'fr-FR' et 'en-US'. Override via opts.locale.
// Source unique de vérité — éviter les copies locales dans les pages.

const LOCALE_MAP = { fr: 'fr-FR', en: 'en-US' };

const getActiveLocale = (override) => {
    if (override) return LOCALE_MAP[override] || override;
    if (typeof document !== 'undefined') {
        const lang = document.documentElement.getAttribute('lang') || 'fr';
        return LOCALE_MAP[lang] || 'fr-FR';
    }
    return 'fr-FR';
};

// Montant FCFA — séparateur de milliers espace insécable, sans suffixe.
// Usage : formatMontant(1234567) -> "1 234 567"
//         formatMontant(1234567, { suffix: ' F' }) -> "1 234 567 F"
//         formatMontant(null, { fallback: '-' }) -> "-"
//         formatMontant(1000, { locale: 'en' }) -> "1,000" (séparateurs US)
export const formatMontant = (val, opts = {}) => {
    const { suffix = '', fallback = '0', locale } = opts;
    if (val == null || val === '' || Number.isNaN(Number(val))) return fallback;
    return new Intl.NumberFormat(getActiveLocale(locale)).format(Number(val)) + suffix;
};

// Format compact pour PDF (jsPDF Helvetica ne gère pas tous les espaces Unicode).
// Toujours en FR (les PDFs métier restent en français même si l'UI est en EN).
export const formatMontantPdf = (val, opts = {}) => formatMontant(val, { ...opts, locale: 'fr' }).replace(/\s/g, ' ');

// Téléphone CI : retire l'indicatif 225 et regroupe par paires.
// Usage : formatPhone('+22507123456') -> "07 12 34 56" (8 chiffres),
//         formatPhone('+2250712345678') -> "07 12 34 56 78" (10 chiffres),
//         formatPhone(null) -> "N/A"
export const formatPhone = (tel, opts = {}) => {
    const { fallback = 'N/A' } = opts;
    if (!tel) return fallback;
    const digits = String(tel).replace(/\D/g, '').replace(/^225/, '');
    if (!digits) return fallback;
    return digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
};

// Date courte (jj/mm/aaaa en FR, mm/dd/yyyy en EN)
export const formatDate = (date, opts = {}) => {
    const { fallback = 'N/A', locale } = opts;
    if (!date) return fallback;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return fallback;
    return d.toLocaleDateString(getActiveLocale(locale));
};

// Date + heure (format selon locale)
export const formatDateTime = (date, opts = {}) => {
    const { fallback = 'N/A', locale } = opts;
    if (!date) return fallback;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return fallback;
    const loc = getActiveLocale(locale);
    return `${d.toLocaleDateString(loc)} ${d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' })}`;
};

// Date longue (1 janvier 2026 / January 1, 2026)
export const formatDateLong = (date, opts = {}) => {
    const { fallback = 'N/A', locale } = opts;
    if (!date) return fallback;
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) return fallback;
    return d.toLocaleDateString(getActiveLocale(locale), { day: 'numeric', month: 'long', year: 'numeric' });
};

// Nombre simple avec séparateur de milliers (sans devise)
export const formatNumber = (val, opts = {}) => {
    const { fallback = '0', decimals, locale } = opts;
    if (val == null || val === '' || Number.isNaN(Number(val))) return fallback;
    const loc = getActiveLocale(locale);
    const fmt = decimals != null
        ? new Intl.NumberFormat(loc, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
        : new Intl.NumberFormat(loc);
    return fmt.format(Number(val));
};

// Initiales (max 2 lettres) — pour Avatars
export const getInitials = (firstName = '', lastName = '') => {
    const first = (firstName || '').trim();
    const last = (lastName || '').trim();
    return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
};
